import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  FinancialSummary,
  RevenueBreakdown,
  TransactionSummary,
} from '@/models/financial-report.model';

const BILLING_TRANSACTIONS_COLLECTION = 'billingTransactions';
const EXPENSES_COLLECTION = 'expenses';
const WALLET_LEDGER_COLLECTION = 'walletLedger';
const EMPLOYERS_COLLECTION = 'employers';
const PROVIDERS_COLLECTION = 'providers';

export class FinancialService {
  /**
   * Get financial summary for a period
   */
  static async getFinancialSummary(
    startDate: Date,
    endDate: Date
  ): Promise<FinancialSummary> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    // Get revenue from billing transactions
    const revenueQuery = query(
      collection(db, BILLING_TRANSACTIONS_COLLECTION),
      where('transactionDate', '>=', start),
      where('transactionDate', '<=', end),
      where('status', '==', 'paid')
    );
    const revenueSnapshot = await getDocs(revenueQuery);
    
    let totalRevenue = 0;
    let subscriptionRevenue = 0;
    let transactionFees = 0;
    let otherRevenue = 0;

    revenueSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const amount = data.amount || 0;
      totalRevenue += amount;
      
      if (data.type === 'employerSubscription' || data.type === 'providerSubscription') {
        subscriptionRevenue += amount;
      } else if (data.type === 'employerClaimFee' || data.type === 'providerTransactionFee') {
        transactionFees += amount;
      } else {
        otherRevenue += amount;
      }
    });

    // Get expenses
    const expensesQuery = query(
      collection(db, EXPENSES_COLLECTION),
      where('expenseDate', '>=', start),
      where('expenseDate', '<=', end),
      where('status', '==', 'paid')
    );
    const expensesSnapshot = await getDocs(expensesQuery);
    
    let totalExpenses = 0;
    let payrollExpenses = 0;
    let infrastructureExpenses = 0;
    let marketingExpenses = 0;
    let otherExpenses = 0;

    expensesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const amount = data.amount || 0;
      totalExpenses += amount;
      
      switch (data.category) {
        case 'payroll':
          payrollExpenses += amount;
          break;
        case 'infrastructure':
          infrastructureExpenses += amount;
          break;
        case 'marketing':
          marketingExpenses += amount;
          break;
        default:
          otherExpenses += amount;
      }
    });

    const netIncome = totalRevenue - totalExpenses;
    const grossMargin = totalRevenue > 0 ? (netIncome / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      subscriptionRevenue,
      transactionFees,
      otherRevenue,
      totalExpenses,
      payrollExpenses,
      infrastructureExpenses,
      marketingExpenses,
      otherExpenses,
      netIncome,
      grossMargin,
      periodStart: startDate,
      periodEnd: endDate,
      periodType: 'monthly', // Can be calculated based on date range
    };
  }

  /**
   * Get revenue breakdown
   */
  static async getRevenueBreakdown(
    startDate: Date,
    endDate: Date
  ): Promise<RevenueBreakdown> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    const revenueQuery = query(
      collection(db, BILLING_TRANSACTIONS_COLLECTION),
      where('transactionDate', '>=', start),
      where('transactionDate', '<=', end),
      where('status', '==', 'paid')
    );
    const revenueSnapshot = await getDocs(revenueQuery);

    const bySource = {
      employerSubscriptions: 0,
      providerSubscriptions: 0,
      transactionFees: 0,
      platformFees: 0,
      other: 0,
    };

    const byEmployerMap = new Map<string, { employerId: string; employerName: string; amount: number }>();
    const byProviderMap = new Map<string, { providerId: string; providerName: string; amount: number }>();
    const trends: Array<{ date: Date; amount: number }> = [];

    // Get employers and providers for names
    const employersSnapshot = await getDocs(collection(db, EMPLOYERS_COLLECTION));
    const employersMap = new Map<string, string>();
    employersSnapshot.docs.forEach(doc => {
      employersMap.set(doc.id, doc.data().name || 'Unknown');
    });

    const providersSnapshot = await getDocs(collection(db, PROVIDERS_COLLECTION));
    const providersMap = new Map<string, string>();
    providersSnapshot.docs.forEach(doc => {
      providersMap.set(doc.id, doc.data().name || 'Unknown');
    });

    revenueSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const amount = data.amount || 0;
      const date = data.transactionDate?.toDate() || new Date();

      // Categorize by type
      if (data.type === 'employerSubscription') {
        bySource.employerSubscriptions += amount;
        if (data.employerId) {
          const existing = byEmployerMap.get(data.employerId) || { employerId: data.employerId, employerName: employersMap.get(data.employerId) || 'Unknown', amount: 0 };
          existing.amount += amount;
          byEmployerMap.set(data.employerId, existing);
        }
      } else if (data.type === 'providerSubscription') {
        bySource.providerSubscriptions += amount;
        if (data.providerId) {
          const existing = byProviderMap.get(data.providerId) || { providerId: data.providerId, providerName: providersMap.get(data.providerId) || 'Unknown', amount: 0 };
          existing.amount += amount;
          byProviderMap.set(data.providerId, existing);
        }
      } else if (data.type === 'employerClaimFee' || data.type === 'providerTransactionFee') {
        bySource.transactionFees += amount;
      } else {
        bySource.other += amount;
      }

      // Add to trends (group by day)
      const dayKey = date.toISOString().split('T')[0];
      const existingTrend = trends.find(t => t.date.toISOString().split('T')[0] === dayKey);
      if (existingTrend) {
        existingTrend.amount += amount;
      } else {
        trends.push({ date, amount });
      }
    });

    return {
      bySource,
      byEmployer: Array.from(byEmployerMap.values()).sort((a, b) => b.amount - a.amount),
      byProvider: Array.from(byProviderMap.values()).sort((a, b) => b.amount - a.amount),
      trends: trends.sort((a, b) => a.date.getTime() - b.date.getTime()),
    };
  }

  /**
   * Get transaction summary
   */
  static async getTransactionSummary(
    startDate: Date,
    endDate: Date
  ): Promise<TransactionSummary> {
    const start = Timestamp.fromDate(startDate);
    const end = Timestamp.fromDate(endDate);

    const ledgerQuery = query(
      collection(db, WALLET_LEDGER_COLLECTION),
      where('createdAt', '>=', start),
      where('createdAt', '<=', end)
    );
    const ledgerSnapshot = await getDocs(ledgerQuery);

    let totalAmount = 0;
    let successfulTransactions = 0;
    let failedTransactions = 0;
    let pendingTransactions = 0;
    const byType = {
      employerPayments: 0,
      memberReimbursements: 0,
      providerPayouts: 0,
      subscriptionPayments: 0,
    };

    ledgerSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const amount = data.amount || 0;
      
      if (data.status === 'completed') {
        totalAmount += amount;
        successfulTransactions++;
      } else if (data.status === 'failed') {
        failedTransactions++;
      } else {
        pendingTransactions++;
      }

      // Categorize by type (this would need to be adjusted based on actual ledger structure)
      if (data.type === 'reimbursement') {
        byType.memberReimbursements += amount;
      }
    });

    return {
      totalTransactions: ledgerSnapshot.docs.length,
      totalAmount,
      successfulTransactions,
      failedTransactions,
      pendingTransactions,
      averageTransactionAmount: successfulTransactions > 0 ? totalAmount / successfulTransactions : 0,
      byType,
    };
  }
}

